// src/context/MealMateContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { message } from "antd";
import { authService } from "../services/authService";
import { api, endpoints } from "../utils/axiosInstance";

const MealMateContext = createContext();

export const useMealMate = () => {
  const context = useContext(MealMateContext);
  if (!context) {
    throw new Error("useMealMate must be used within a MealMateProvider");
  }
  return context;
};

export const MealMateProvider = ({ children }) => {
  console.log("MealMateProvider rendered");
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [favoriteRecipesNeedRefresh, setFavoriteRecipesNeedRefresh] = useState(false);

  const triggerFavoriteRecipesRefresh = useCallback(() => {
    setFavoriteRecipesNeedRefresh((prev) => !prev);
  }, []);

  const normalizeNotificationSettings = (notificationSettings) => {
    const defaultSettings = {
      enabled: false,
      frequency: "daily",
      types: [],
      mealReminders: [
        { mealType: "breakfast", time: "07:00", enabled: false, reminderMinutes: 10 },
        { mealType: "lunch", time: "12:00", enabled: false, reminderMinutes: 10 },
        { mealType: "dinner", time: "18:00", enabled: false, reminderMinutes: 10 },
        { mealType: "snack", time: "15:00", enabled: false, reminderMinutes: 10 },
      ],
      generalReminder: { enabled: false, time: "09:00" },
    };

    if (!notificationSettings || typeof notificationSettings !== "object") {
      return defaultSettings;
    }

    const normalizedMealReminders = defaultSettings.mealReminders.map(defaultMeal => {
      const existingMeal = notificationSettings.mealReminders?.find(
        meal => meal.mealType === defaultMeal.mealType
      );
      return existingMeal
        ? {
            mealType: existingMeal.mealType,
            time: existingMeal.time || defaultMeal.time,
            enabled: Boolean(existingMeal.enabled),
            reminderMinutes: existingMeal.reminderMinutes || defaultMeal.reminderMinutes,
          }
        : defaultMeal;
    });

    return {
      enabled: Boolean(notificationSettings.enabled),
      frequency: notificationSettings.frequency || defaultSettings.frequency,
      types: Array.isArray(notificationSettings.types) ? notificationSettings.types : defaultSettings.types,
      mealReminders: normalizedMealReminders,
      generalReminder: {
        enabled: Boolean(notificationSettings.generalReminder?.enabled),
        time: notificationSettings.generalReminder?.time || defaultSettings.generalReminder.time,
      },
    };
  };

  const fetchUserProfile = useCallback(async () => {
    console.log("MealMateContext: fetchUserProfile được gọi.");
    setLoading(true);
    try {
      const token = sessionStorage.getItem("token");
      console.log("MealMateContext: Token:", token ? "Có token" : "Không có token");

      if (!token) {
        console.log("MealMateContext: Không có token, đặt user null.");
        setUser(null);
        setIsAuthenticated(false);
        sessionStorage.removeItem("userInfo");
        setLoading(false);
        return;
      }

      console.log("MealMateContext: Gọi API /users/profile...");
      const response = await api.get(endpoints.user.profile);
      const userProfileData = response?.data?.user || response?.data || response;

      console.log("MealMateContext: userProfileData:", userProfileData);
      console.log("MealMateContext: notificationSettings từ API:", userProfileData.notificationSettings);

      if (userProfileData) {
        const notificationSettings = normalizeNotificationSettings(userProfileData.notificationSettings);
        const lastNotificationSent = userProfileData.lastNotificationSent || {};

        const userInfo = {
          _id: userProfileData._id,
          accountId: userProfileData.accountId,
          email: userProfileData.email,
          role: userProfileData.role,
          authProvider: userProfileData.authProvider,
          isActive: userProfileData.isActive,
          full_name: userProfileData.full_name,
          profile_picture: userProfileData.profile_picture,
          phone: userProfileData.phone,
          gender: userProfileData.gender,
          date_of_birth: userProfileData.date_of_birth,
          job: userProfileData.job,
          height: userProfileData.height,
          weight: userProfileData.weight,
          activity_level: userProfileData.activity_level,
          goal: userProfileData.goal,
          calorieGoal: userProfileData.calorieGoal,
          proteinGoal: userProfileData.proteinGoal,
          fatGoal: userProfileData.fatGoal,
          carbohydrateGoal: userProfileData.carbohydrateGoal,
          notificationSettings,
          lastNotificationSent,
          healthGoal: userProfileData.healthGoal,
          activityLevel: userProfileData.activityLevel,
          dietaryPreferences: userProfileData.dietaryPreferences,
          allergies: userProfileData.allergies,
          otherAllergies: userProfileData.otherAllergies,
          healthConditions: userProfileData.healthConditions,
          otherHealthConditions: userProfileData.otherHealthConditions,
          mealCountPerDay: userProfileData.mealCountPerDay,
          preferredMealTimes: userProfileData.preferredMealTimes,
          isPremium: userProfileData.isPremium,
          isSubscribed: userProfileData.isSubscribed,
          subscriptionEndDate: userProfileData.subscriptionEndDate,
        };

        console.log("MealMateContext: Cập nhật user state:", userInfo);
        setUser(userInfo);
        setIsAuthenticated(true);
        sessionStorage.setItem("userInfo", JSON.stringify(userInfo));
      } else {
        console.log("MealMateContext: Không có userProfileData, đăng xuất.");
        setUser(null);
        setIsAuthenticated(false);
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("userInfo");
        message.error("Không thể tải thông tin người dùng.");
      }
    } catch (error) {
      console.error("MealMateContext: Lỗi fetchUserProfile:", error);
      setUser(null);
      setIsAuthenticated(false);
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("userInfo");
      message.error("Lỗi khi tải thông tin người dùng. Vui lòng đăng nhập lại.");
    } finally {
      setLoading(false);
      console.log("MealMateContext: fetchUserProfile hoàn tất.");
    }
  }, []);

  useEffect(() => {
    console.log("MealMateContext: useEffect khởi tạo auth.");
    const initializeAuth = async () => {
      const token = sessionStorage.getItem("token");
      if (token) {
        console.log("MealMateContext: Có token, gọi fetchUserProfile.");
        await fetchUserProfile();
      } else {
        console.log("MealMateContext: Không có token, reset trạng thái.");
        setUser(null);
        setIsAuthenticated(false);
        setLoading(false);
      }
    };

    initializeAuth();
  }, [fetchUserProfile]);

  const register = async (userData) => {
    try {
      const response = await authService.register(userData);
      if (response && response.accessToken) {
        sessionStorage.setItem("token", response.accessToken);
        setIsAuthenticated(true);
        setShowLoginModal(false);
        await fetchUserProfile();
        message.success("Đăng ký và đăng nhập thành công!");
      } else {
        message.error("Đăng ký thất bại: Không nhận được accessToken.");
      }
      return response;
    } catch (error) {
      message.error("Đăng ký thất bại: " + (error.response?.data?.message || error.message));
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      if (response && response.accessToken) {
        sessionStorage.setItem("token", response.accessToken);
        setIsAuthenticated(true);
        setShowLoginModal(false);
        await fetchUserProfile();
        message.success("Đăng nhập thành công!");
      } else {
        message.error("Đăng nhập thất bại: Không nhận được accessToken.");
      }
      return response;
    } catch (error) {
      message.error("Đăng nhập thất bại: " + (error.response?.data?.message || error.message));
      throw error;
    }
  };

  const logout = useCallback(() => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("userInfo");
    setUser(null);
    setIsAuthenticated(false);
    setLoading(false);
    message.info("Bạn đã đăng xuất.");
    console.log("MealMateContext: User đã đăng xuất.");
  }, []);

  const handleGoogleLogin = useCallback(async () => {
    window.location.href = `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/api/auth/google`;
  }, []);

  const handleGoogleCallback = useCallback(async () => {
    console.log("MealMateContext: handleGoogleCallback được gọi.");
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get("token");

      if (token) {
        sessionStorage.setItem("token", token);
        setIsAuthenticated(true);
        setShowLoginModal(false);
        await fetchUserProfile();
        message.success("Đăng nhập bằng Google thành công!");
      } else {
        console.warn("MealMateContext: Không tìm thấy token trong Google callback.");
        setUser(null);
        setIsAuthenticated(false);
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("userInfo");
        message.error("Đăng nhập Google thất bại!");
      }
    } catch (error) {
      console.error("MealMateContext: Lỗi trong handleGoogleCallback:", error);
      setUser(null);
      setIsAuthenticated(false);
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("userInfo");
      message.error("Đăng nhập Google thất bại!");
      throw error;
    }
  }, [fetchUserProfile]);

  const updateUserProfile = useCallback((newUserData) => {
    console.log("MealMateContext: Cập nhật user profile:", newUserData);
    setUser((prev) => {
      const updatedUser = { ...prev, ...newUserData };
      sessionStorage.setItem("userInfo", JSON.stringify(updatedUser));
      return updatedUser;
    });
  }, []);

  const value = {
    user,
    setUser,
    isAuthenticated,
    setIsAuthenticated,
    loading,
    showLoginModal,
    setShowLoginModal,
    login,
    logout,
    register,
    handleGoogleLogin,
    handleGoogleCallback,
    updateUserProfile,
    triggerFavoriteRecipesRefresh,
    favoriteRecipesNeedRefresh,
    fetchUserProfile,
  };

  return (
    <MealMateContext.Provider value={value}>
      {children}
    </MealMateContext.Provider>
  );
};